import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { X, Brain, AlertTriangle, CheckCircle, Info } from 'lucide-react';

interface AIAnalysisResult {
  urunKartId: number;
  urunAdi: string;
  consistent: boolean;
  inconsistencyScore: number;
  reason: string;  // Tutarsızlık sebebi
  checkedAt?: string;
}

interface AIAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  result: AIAnalysisResult | null;
  loading?: boolean;
  error?: string | null;
}

const AIAnalysisModal: React.FC<AIAnalysisModalProps> = ({
  isOpen,
  onClose,
  result,
  loading = false,
  error = null,
}) => {
  // Escape tuşu ile kapatma - Hook'lar her zaman en başta çağrılmalı
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Early return - Hook'lardan sonra yapılmalı
  if (!isOpen) return null;

  // Skor rengini belirle
  const getScoreColor = (score: number) => {
    if (score === 0) return 'text-green-600 bg-green-100';
    if (score <= 30) return 'text-green-600 bg-green-100';
    if (score <= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  // Skor durumunu belirle
  const getScoreLabel = (score: number) => {
    if (score === 0) return 'Tutarlı';
    if (score <= 30) return 'Düşük Tutarsızlık';
    if (score <= 60) return 'Orta Tutarsızlık';
    return 'Yüksek Tutarsızlık';
  };

  // Backdrop tıklama ile kapatma
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 border-b bg-gradient-to-r from-purple-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Brain className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <CardTitle className="text-lg">AI Tutarsızlık Analizi</CardTitle>
              {result && (
                <p className="text-sm text-muted-foreground mt-1 max-w-md truncate">
                  {result.urunAdi}
                </p>
              )}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
              <p className="text-muted-foreground">AI analiz yapılıyor...</p>
              <p className="text-xs text-muted-foreground mt-1">Bu işlem birkaç saniye sürebilir</p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-5 w-5" />
                <p className="font-medium">Analiz Hatası</p>
              </div>
              <p className="text-sm text-red-600 mt-2">{error}</p>
            </div>
          )}

          {/* Result State */}
          {result && !loading && !error && (
            <>
              {/* Skor ve Durum */}
              <div className="flex items-center gap-4">
                <div className={`flex items-center gap-2 px-4 py-3 rounded-lg ${getScoreColor(result.inconsistencyScore)}`}>
                  {result.inconsistencyScore === 0 ? (
                    <CheckCircle className="h-6 w-6" />
                  ) : (
                    <AlertTriangle className="h-6 w-6" />
                  )}
                  <div>
                    <p className="text-2xl font-bold">{result.inconsistencyScore}%</p>
                    <p className="text-xs font-medium">{getScoreLabel(result.inconsistencyScore)}</p>
                  </div>
                </div>

                <div className="flex-1">
                  {result.checkedAt && (
                    <p className="text-xs text-muted-foreground">
                      Kontrol: {new Date(result.checkedAt).toLocaleString('tr-TR')}
                    </p>
                  )}
                </div>
              </div>

              {/* Tutarlı Durum */}
              {result.consistent && (
                <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  <div>
                    <p className="font-medium text-green-700">Tutarlı</p>
                    <p className="text-sm text-green-600">
                      {result.reason || 'Ürün adı ve açıklamaları tutarlı görünüyor.'}
                    </p>
                  </div>
                </div>
              )}

              {/* Tutarsız Durum - Sebep */}
              {!result.consistent && result.reason && (
                <div className="space-y-2">
                  <h4 className="font-medium flex items-center gap-2">
                    <Info className="h-4 w-4 text-orange-500" />
                    Tutarsızlık Sebebi
                  </h4>
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <p className="text-sm text-orange-800">{result.reason}</p>
                  </div>
                </div>
              )}

              {/* Ürün Bilgisi */}
              <div className="pt-4 border-t">
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium">Ticimax ID:</span> {result.urunKartId}
                </p>
              </div>
            </>
          )}
        </CardContent>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50">
          <Button onClick={onClose} className="w-full" variant="outline">
            Kapat
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default AIAnalysisModal;
